/**
 * Newsletter subscription popup + promo (success) popup.
 *
 * Submit model: the form submits NATIVELY (no fetch interception) so Shopify's
 * invisible CAPTCHA can attach its token. This element only does client-side
 * validation on the submit event: invalid → preventDefault + show error; valid
 * → let the native submit through. Shopify POSTs to /contact, then 302s back
 * to this page with ?customer_posted=true. The promo popup (rendered site-wide
 * in the header) detects that flag on load and opens the success state — see
 * assets/newsletter-form.js.
 */
class NewsletterSubscriptionPopup extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.root = this.closest("[data-newsletter-subscription-popup]");
    this.form = this.querySelector("form");
    this.email = this.querySelector("[data-newsletter-popup-email]");
    this.consent = this.querySelector("[data-newsletter-popup-consent]");
    this.submit = this.querySelector("[data-newsletter-popup-submit]");
    this.error = this.querySelector("[data-newsletter-popup-error]");
    this.previousActiveElement = null;
    this.opened = false;
    this.dismissed = false;

    // Bridge for the Image floating window (a separate section): it dispatches
    // `newsletter-popup:open` and we open on its behalf. Registered before the
    // page guards so the handler can re-check them itself.
    this.externalOpenHandler = (event) => {
      if (!this.root || this.isBlockedPage() || this.isOutsideHomepage()) return;
      // Hard gate: a subscribed visitor is never shown the popup, not even
      // from the floating window's forced open.
      if (this.isSubscribed()) return;
      this.open(event?.detail?.trigger || "external", event?.detail?.force !== false);
    };
    window.addEventListener("newsletter-popup:open", this.externalOpenHandler);

    if (!this.root || this.isBlockedPage() || this.isOutsideHomepage()) return;

    this.clearError();
    this.bindEvents();

    if (
      this.isSubscribed() ||
      this.hasDismissedToday("popup") ||
      // A newsletter form on this page was just submitted successfully (native
      // POST redirect-back). Don't re-arm the subscription popup this load.
      window.MarstekNewsletter?.hasJustPosted()
    ) {
      this.clearTriggers();
      return;
    }

    this.startTriggers();
  }

  bindEvents() {
    this.root.querySelectorAll("[data-newsletter-popup-close]").forEach((button) => {
      button.addEventListener("click", () => this.close());
    });

    this.form?.addEventListener("submit", (event) => {
      const result = window.MarstekNewsletter.validate({
        email: this.email,
        consent: this.consent,
      });

      if (result.ok) {
        // Let the native submit through so Shopify's CAPTCHA can run.
        this.clearError();
        this.track("popup_submit");
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError(result.message);
      if (result.field === "email") this.email?.focus();
      else if (result.field === "consent") this.consent?.focus();
    });

    this.email?.addEventListener("input", () => this.clearError());
    this.consent?.addEventListener("change", () => this.clearError());

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.opened) this.close();
    });
  }

  startTriggers() {
    const delay = Math.max(Number(this.root.dataset.triggerDelay) || 8, 1) * 1000;
    const scrollThreshold = Math.min(Math.max(Number(this.root.dataset.triggerScroll) || 30, 1), 99);

    this.delayTimer = window.setTimeout(() => this.open("time"), delay);
    this.scrollHandler = () => {
      const totalScrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalScrollableHeight > 0 && (window.scrollY / totalScrollableHeight) * 100 >= scrollThreshold) {
        this.open("scroll");
      }
    };
    window.addEventListener("scroll", this.scrollHandler, { passive: true });

    if (this.root.dataset.exitIntent === "true" && !window.matchMedia("(max-width: 899px)").matches) {
      // Arm exit intent only after the time-trigger delay has elapsed, so it
      // can't preempt the configured time/scroll triggers. This also avoids
      // constant false positives inside the theme editor, where the mouse
      // frequently leaves the preview frame towards the editor chrome.
      this.exitIntentTimer = window.setTimeout(() => {
        this.exitIntentHandler = (event) => {
          if (event.clientY <= 0) this.open("exit");
        };
        document.addEventListener("mouseout", this.exitIntentHandler);
      }, delay);
    }
  }

  open(trigger, force = false) {
    // Two-level gate:
    //   hard  → subscribed state, force can never bypass it
    //   soft  → auto-trigger guards, a deliberate click on the floating
    //           window (force) may bypass these
    if (this.isSubscribed()) return;
    if (
      !force &&
      (this.opened || this.dismissed || this.hasDismissedToday("popup"))
    ) {
      return;
    }

    // A forced open deliberately does NOT reset this.dismissed, so the auto
    // triggers stay suppressed for the rest of the day.

    this.opened = true;
    this.clearTriggers();
    this.previousActiveElement = document.activeElement;
    this.root.classList.remove("hidden");
    this.root.classList.add("is-open", "wt-popup-fade-in");
    this.root.setAttribute("aria-hidden", "false");
    document.body.classList.add("newsletter-subscription-popup-open");
    this.track("popup_show", { trigger });

    window.requestAnimationFrame(() => {
      (this.email || this.root.querySelector("[data-newsletter-popup-close]"))?.focus();
    });
  }

  close() {
    if (!this.opened) return;

    this.opened = false;
    this.dismissed = true;
    this.markDismissedToday("popup");
    this.root.classList.add("hidden");
    this.root.classList.remove("is-open");
    this.root.setAttribute("aria-hidden", "true");
    document.body.classList.remove("newsletter-subscription-popup-open");
    this.track("popup_close");

    if (this.previousActiveElement?.focus) this.previousActiveElement.focus();
  }

  showError(message) {
    if (!this.error) return;

    this.error.textContent = message;
    this.error.classList.remove("hidden");
  }

  clearError() {
    if (!this.error) return;

    this.error.textContent = "";
    this.error.classList.add("hidden");
  }

  clearTriggers() {
    window.clearTimeout(this.delayTimer);
    window.clearTimeout(this.exitIntentTimer);
    if (this.scrollHandler) window.removeEventListener("scroll", this.scrollHandler);
    if (this.exitIntentHandler) document.removeEventListener("mouseout", this.exitIntentHandler);
  }

  disconnectedCallback() {
    this.clearTriggers();
    if (this.externalOpenHandler) {
      window.removeEventListener("newsletter-popup:open", this.externalOpenHandler);
    }
  }

  isBlockedPage() {
    return /\/checkout(?:\/|$)|\/orders?(?:\/|$)|\/thank[_-]?you(?:\/|$)/i.test(window.location.pathname);
  }

  isOutsideHomepage() {
    return this.root.dataset.homepageOnly === "true" && this.root.dataset.pageIsHome !== "true";
  }

  isPreviewMode() {
    return (
      this.root?.dataset.newsletterTestMode === "true" ||
      window.MarstekNewsletter?.isPreviewMode?.() === true
    );
  }

  // Highest-priority gate. Preview ignores it so merchants can always test.
  isSubscribed() {
    if (this.isPreviewMode()) return false;
    return (
      this.root?.dataset.hasSubscription === "true" ||
      window.MarstekNewsletter?.isSubscribed?.() === true
    );
  }

  hasDismissedToday(kind) {
    return window.MarstekNewsletter?.hasDismissedToday?.(kind) === true;
  }

  markDismissedToday(kind) {
    window.MarstekNewsletter?.markDismissedToday?.(kind);
  }

  track(eventName, detail = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...detail });

    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, detail);
    }

    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
}

class NewsletterPromoPopup extends HTMLElement {
  // Triggers that represent a real, confirmed subscription (write the cookie +
  // close any open subscription popup). `newsletter_posted` is the native-submit
  // success path detected on page load.
  static get SUBSCRIPTION_TRIGGERS() {
    return ["newsletter_banner", "newsletter_popup", "newsletter_posted"];
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.root = this.closest("[data-newsletter-promo-popup]");
    this.previousActiveElement = null;
    this.opened = false;

    if (!this.root) return;

    this.bindEvents();

    // Detect the redirect-back of a successful native newsletter submission
    // (Shopify appends ?customer_posted=true) and open the success state.
    const handled = window.MarstekNewsletter?.handlePostedOnLoad({
      onSuccess: () => this.open("newsletter_posted"),
    });
    if (handled) return;

    if (!this.root.classList.contains("hidden")) {
      this.open("popup");
      return;
    }
  }

  bindEvents() {
    this.root.querySelectorAll("[data-newsletter-promo-close]").forEach((button) => {
      button.addEventListener("click", () => this.close());
    });

    this.root.addEventListener("click", (event) => {
      const copyButton = event.target.closest("[data-newsletter-popup-copy]");
      if (copyButton) this.copyDiscount(copyButton);

      const cta = event.target.closest("[data-newsletter-popup-cta]");
      if (cta) this.track("popup_click");
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.opened) this.close();
    });
  }

  open(trigger = "success") {
    const isSubscriptionTrigger =
      NewsletterPromoPopup.SUBSCRIPTION_TRIGGERS.includes(trigger);

    if (isSubscriptionTrigger) {
      window.MarstekNewsletter?.markSubscribed?.();
    }

    if (this.opened) {
      this.root.classList.remove("hidden");
      this.root.classList.add("is-open");
      this.root.setAttribute("aria-hidden", "false");
      document.body.classList.add("newsletter-subscription-popup-open");
      return;
    }

    if (isSubscriptionTrigger) {
      const subscriptionPopup = document.querySelector(
        "[data-newsletter-subscription-popup] newsletter-subscription-popup",
      );
      subscriptionPopup?.close();
    }

    this.opened = true;
    if (trigger === "newsletter_banner") {
      document.querySelector("[data-newsletter-banner-posted-successfully='true']")?.setAttribute("hidden", "");
    }
    this.previousActiveElement = document.activeElement;
    this.root.classList.remove("hidden");
    this.root.classList.add("is-open", "wt-popup-fade-in");
    this.root.setAttribute("aria-hidden", "false");
    document.body.classList.add("newsletter-subscription-popup-open");
    this.track("popup_success");
    this.track("popup_show", { trigger });

    window.requestAnimationFrame(() => {
      this.root.querySelector("[data-newsletter-promo-close], [data-newsletter-popup-copy]")?.focus();
    });
  }

  close() {
    if (!this.opened) return;

    this.opened = false;
    this.root.classList.add("hidden");
    this.root.classList.remove("is-open");
    this.root.setAttribute("aria-hidden", "true");
    document.body.classList.remove("newsletter-subscription-popup-open");
    this.track("popup_close");

    if (this.previousActiveElement?.focus) this.previousActiveElement.focus();
  }

  async copyDiscount(button) {
    const value = button.dataset.copyValue;
    if (!value || button.disabled) return;

    const label = button.querySelector("[data-newsletter-popup-copy-label]");
    const copyIcon = button.querySelector("[data-newsletter-popup-copy-icon]");
    const loader = button.querySelector("[data-newsletter-popup-copy-loader]");
    const successIcon = button.querySelector("[data-newsletter-popup-copy-success-icon]");

    button.disabled = true;
    copyIcon?.setAttribute("hidden", "");
    loader?.removeAttribute("hidden");
    if (label) label.textContent = "Copying";
    button.setAttribute("aria-label", "Copying");

    try {
      await Promise.all([
        navigator.clipboard.writeText(value),
        new Promise((resolve) => window.setTimeout(resolve, 350)),
      ]);
    } catch (error) {
      const discount = this.querySelector("[data-newsletter-popup-discount]");
      const range = document.createRange();
      range.selectNodeContents(discount);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      document.execCommand("copy");
      await new Promise((resolve) => window.setTimeout(resolve, 350));
    }

    loader?.setAttribute("hidden", "");
    successIcon?.removeAttribute("hidden");
    successIcon?.setAttribute("aria-hidden", "false");
    if (label) label.textContent = "Copied";
    button.setAttribute("aria-label", "Copied");
  }

  track(eventName, detail = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...detail });

    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, detail);
    }

    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
}

if (!customElements.get("newsletter-subscription-popup")) {
  customElements.define("newsletter-subscription-popup", NewsletterSubscriptionPopup);
}

if (!customElements.get("newsletter-promo-popup")) {
  customElements.define("newsletter-promo-popup", NewsletterPromoPopup);
}
